<template>
  <div class="event-running-time" :class="`event-running-time--${phase}`">
    <div class="event-running-time__row">
      <span class="event-running-time__left">
        <span class="event-running-time__dot" />
        <span class="event-running-time__title">Running time</span>
      </span>
      <span class="event-running-time__right">
        <span class="event-running-time__phase">{{ phaseLabel }}</span>
        <span class="event-running-time__clock">{{ clock }}</span>
      </span>
    </div>

    <div class="event-running-time__bar">
      <div class="event-running-time__fill" :style="{ width: `${ratio * 100}%` }" />
      <span class="event-running-time__tick" :style="{ left: `${(TARGET_SECONDS / MAXIMUM_SECONDS) * 100}%` }" />
      <span class="event-running-time__tick" :style="{ left: `${(WARN_SECONDS / MAXIMUM_SECONDS) * 100}%` }" />
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

// How long the whole sale has been running, on the same pace-bar idea as
// `TimeOnBlock` but a scale up: two hours is a normal lane, three is running
// long, and past that the sale is behind schedule.
//
// Kept as its own component rather than a `TimeOnBlock` with different
// thresholds. The two read the same and share no code on purpose — the phase
// LABELS differ ("Move it" is a message to the clerk about one car; "Behind
// schedule" is a message about the whole sale), and a shared component would
// need every one of them passed in, which is more surface than the duplication.
defineOptions({ name: 'EventRunningTime' });

const props = defineProps({
  // Seconds the sale had already been running when this mounted.
  startedAtSeconds: { type: Number, default: 0 },
});

const TARGET_SECONDS = 2 * 3600;
const WARN_SECONDS = 3 * 3600;
const MAXIMUM_SECONDS = 4 * 3600;

const seconds = ref(props.startedAtSeconds);
let timer = null;
onMounted(() => {
  timer = setInterval(() => {
    seconds.value += 1;
  }, 1000);
});
onBeforeUnmount(() => clearInterval(timer));

const ratio = computed(() => Math.min(1, seconds.value / MAXIMUM_SECONDS));

const phase = computed(() => {
  if (seconds.value < TARGET_SECONDS) return 'calm';
  if (seconds.value < WARN_SECONDS) return 'warn';
  return 'urgent';
});

const PHASE_LABELS = { calm: 'On pace', warn: 'Running long', urgent: 'Behind schedule' };
const phaseLabel = computed(() => PHASE_LABELS[phase.value]);

// Drops the hours field below an hour, so a lane that just opened does not read
// "0:04:12" — the leading zero is noise on a clock nobody reads that precisely.
const clock = computed(() => {
  const hours = Math.floor(seconds.value / 3600);
  const minutes = Math.floor((seconds.value % 3600) / 60);
  const remainder = seconds.value % 60;
  const paddedSeconds = String(remainder).padStart(2, '0');
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`
    : `${minutes}:${paddedSeconds}`;
});
</script>

<style scoped>
.event-running-time {
  display: flex;
  flex-direction: column;
  min-width: 280px;
  gap: var(--p-event-running-time-gap);
  padding: var(--p-event-running-time-padding);
}

.event-running-time__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.event-running-time__left {
  display: inline-flex;
  align-items: center;
  gap: var(--p-event-running-time-left-gap);
}

.event-running-time__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--p-event-running-time-dot-border-radius);
  background: var(--p-event-running-time-dot-background);
}

.event-running-time--warn .event-running-time__dot {
  background: var(--p-event-running-time-warn-dot-background);
  box-shadow: var(--p-event-running-time-warn-dot-box-shadow);
}

.event-running-time--urgent .event-running-time__dot {
  background: var(--p-event-running-time-urgent-dot-background);
  box-shadow: var(--p-event-running-time-urgent-dot-box-shadow);
  animation: event-running-time-pulse 1.3s infinite;
}

.event-running-time__title {
  font-size: var(--p-event-running-time-title-font-size);
  font-weight: var(--p-event-running-time-title-font-weight);
  letter-spacing: var(--p-event-running-time-title-letter-spacing);
  color: var(--p-event-running-time-title-color);
  text-transform: uppercase;
}

.event-running-time__right {
  display: inline-flex;
  align-items: baseline;
  gap: var(--p-event-running-time-right-gap);
}

.event-running-time__phase {
  font-size: var(--p-event-running-time-phase-font-size);
  font-weight: var(--p-event-running-time-phase-font-weight);
  letter-spacing: var(--p-event-running-time-phase-letter-spacing);
  color: var(--p-event-running-time-phase-color);
  text-transform: uppercase;
}

.event-running-time--warn .event-running-time__phase {
  color: var(--p-event-running-time-warn-phase-color);
}

.event-running-time--urgent .event-running-time__phase {
  color: var(--p-event-running-time-urgent-phase-color);
}

.event-running-time__clock {
  font-family: var(--p-event-running-time-clock-font-family);
  font-size: var(--p-event-running-time-clock-font-size);
  font-weight: var(--p-event-running-time-clock-font-weight);
  letter-spacing: var(--p-event-running-time-clock-letter-spacing);
  color: var(--p-event-running-time-clock-color);
  font-variant-numeric: tabular-nums;
}

.event-running-time--warn .event-running-time__clock {
  color: var(--p-event-running-time-warn-clock-color);
}

.event-running-time--urgent .event-running-time__clock {
  color: var(--p-event-running-time-urgent-clock-color);
}

.event-running-time__bar {
  height: 4px;
  overflow: hidden;
  position: relative;
  border-radius: var(--p-event-running-time-bar-border-radius);
  background: var(--p-event-running-time-bar-background);
}

.event-running-time__fill {
  height: 100%;
  transition: all 400ms cubic-bezier(0.2, 0, 0, 1);
  background: var(--p-event-running-time-fill-background);
}

.event-running-time--warn .event-running-time__fill {
  background: var(--p-event-running-time-warn-fill-background);
}

.event-running-time--urgent .event-running-time__fill {
  background: var(--p-event-running-time-urgent-fill-background);
}

.event-running-time__tick {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--p-event-running-time-tick-background);
}

@keyframes event-running-time-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.85);
    opacity: 0.65;
  }
}

</style>
